import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { thumb } from "../lib/api";
import { Clock, Users, ChevronLeft, ChevronRight, ArrowRight, CalendarDays } from "lucide-react";
import { AddToCalendar } from "../components/AddToCalendar";

const CATEGORIES = ["Adult Classes", "Kids Camps", "Guest Creator", "Open Studio", "Membership Events"];
const CAT = {
  "Adult Classes":     { color: "#9BCC14", chip: "bg-[#C6FF33] text-[#121212]" },
  "Kids Camps":        { color: "#E0218A", chip: "bg-[#E0218A] text-white" },
  "Guest Creator":     { color: "#2D6BFF", chip: "bg-[#2D6BFF] text-white" },
  "Open Studio":       { color: "#F5A623", chip: "bg-[#F5A623] text-[#121212]" },
  "Membership Events": { color: "#7C3AED", chip: "bg-[#7C3AED] text-white" },
  "Special Events":    { color: "#14B8A6", chip: "bg-[#14B8A6] text-white" },
};
const catColor = (c) => (CAT[c] || CAT["Adult Classes"]).color;
const catChip = (c) => (CAT[c] || CAT["Adult Classes"]).chip;
// Filter-tab label (plural) vs stored category value / badge (singular)
const catLabel = (c) => ({ "Guest Creator": "Guest Creators" }[c] || c);

const pad = (n) => String(n).padStart(2, "0");
const isoLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseISO = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d, 12); };
const prettyDate = (s) => parseISO(s).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

const classToEvent = (c) => ({
  ...c, type: "class", category: c.category || "Adult Classes", link: `/classes/${c.slug || c.id}`,
  spots: (c.capacity || 0) - (c.seats_booked || 0), soldOut: (c.seats_booked || 0) >= (c.capacity || 0),
});

const BANNERS = {
  "Guest Creator": {
    desc: "Local guest creators teach unique hands-on workshops throughout the month.",
    actions: [{ label: "Learn More", to: "/guest-creators", primary: true }, { label: "Apply to Teach", to: "/guest-creators", primary: false }],
  },
};

function EventCard({ e }) {
  return (
    <Link to={e.link} data-testid={`class-card-${e.id}`} className="group bg-white border-2 border-[#121212] hover:-translate-y-1 transition-transform duration-300 flex flex-col">
      <div className="aspect-[4/3] overflow-hidden relative">
        <img src={thumb(e.image_url, 600)} alt={e.title} className={`w-full h-full object-cover transition-transform duration-700 ${e.soldOut ? "grayscale-[35%] scale-105" : "group-hover:scale-105"}`} />
        <span className={`absolute top-4 left-4 px-3 py-1 text-[10px] tracking-[0.18em] uppercase font-bold ${catChip(e.category)}`}>{e.category}</span>
        {e.soldOut && (
          <div data-testid={`sold-out-overlay-${e.id}`} className="absolute inset-0 bg-[#121212]/55 flex items-center justify-center">
            <span className="px-6 py-2.5 bg-[#E0218A] text-white font-serif font-black text-2xl tracking-[0.12em] uppercase -rotate-[8deg] border-2 border-white shadow-[0_6px_0_rgba(0,0,0,0.35)]">
              Sold Out
            </span>
          </div>
        )}
      </div>
      <div className="p-6 flex flex-col flex-1">
        <p className="text-xs font-sans tracking-[0.2em] uppercase text-[#121212]">{prettyDate(e.date)}{e.time ? ` · ${e.time}` : ""}</p>
        <h3 className="font-serif font-black text-2xl text-[#121212] mt-2 leading-tight">{e.title}</h3>
        {e.description && <p className="mt-3 text-sm text-stone-600 line-clamp-2">{e.description}</p>}
        <div className="mt-4 flex flex-wrap gap-4 text-stone-500 text-sm">
          <span className="inline-flex items-center gap-1.5"><Clock size={14} /> {e.duration}</span>
          {e.type === "class"
            ? (e.soldOut
                ? <span data-testid={`seats-${e.id}`} className="inline-flex items-center gap-1.5 text-[#E0218A] font-black uppercase tracking-wide"><Users size={14} /> Sold out</span>
                : e.spots <= 3
                  ? <span data-testid={`seats-${e.id}`} className="inline-flex items-center gap-1.5 text-[#E0218A] font-bold"><Users size={14} /> Only {e.spots} left!</span>
                  : <span data-testid={`seats-${e.id}`} className="inline-flex items-center gap-1.5"><Users size={14} /> {e.spots} spots left</span>)
            : <span className="inline-flex items-center gap-1.5"><Users size={14} /> {e.slots?.length || 3} sessions</span>}
        </div>
        <div className="mt-5 pt-5 border-t border-stone-200 flex justify-between items-center">
          <span className="font-serif font-black text-2xl text-[#121212]">{e.type === "open_studio" ? `from $${e.price}` : `$${e.price}`}</span>
          <span className={`inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.2em] uppercase font-bold transition-colors ${e.soldOut ? "bg-white text-[#121212] border-2 border-[#121212]" : "bg-[#121212] text-[#F2E9D8] group-hover:bg-black"}`}>
            {e.soldOut ? "View" : "Book Now"} <ArrowRight size={13} />
          </span>
        </div>
        <div className="mt-3"><AddToCalendar event={e} /></div>
      </div>
    </Link>
  );
}

function ScheduleCalendar({ classes }) {
  const [view, setView] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selected, setSelected] = useState(() => isoLocal(new Date()));

  const eventsOnDate = (d) => {
    const iso = isoLocal(d);
    return classes.map(classToEvent).filter((e) => e.date === iso);
  };

  const { cells, year, month } = useMemo(() => {
    const y = view.getFullYear(), m = view.getMonth();
    const startDay = new Date(y, m, 1).getDay();
    const total = new Date(y, m + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < startDay; i++) arr.push(null);
    for (let day = 1; day <= total; day++) arr.push(new Date(y, m, day, 12));
    return { cells: arr, year: y, month: m };
  }, [view]);

  const todayIso = isoLocal(new Date());
  const selectedEvents = selected ? eventsOnDate(parseISO(selected)) : [];
  const monthLabel = new Date(year, month, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });

  return (
    <div className="grid lg:grid-cols-5 gap-8" data-testid="class-schedule-calendar">
      <div className="lg:col-span-3 bg-white border-2 border-[#121212] p-5 sm:p-7">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif font-black text-2xl text-[#121212]">{monthLabel}</h3>
          <div className="flex gap-2">
            <button data-testid="cal-prev" onClick={() => setView(new Date(year, month - 1, 1))} className="w-10 h-10 border-2 border-[#121212] flex items-center justify-center hover:bg-[#121212] hover:text-[#F2E9D8] transition-colors"><ChevronLeft size={18} /></button>
            <button data-testid="cal-next" onClick={() => setView(new Date(year, month + 1, 1))} className="w-10 h-10 border-2 border-[#121212] flex items-center justify-center hover:bg-[#121212] hover:text-[#F2E9D8] transition-colors"><ChevronRight size={18} /></button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="text-[10px] tracking-[0.15em] uppercase font-bold text-stone-400 pb-2">{d}</div>
          ))}
          {cells.map((d, i) => {
            if (!d) return <div key={`b${i}`} />;
            const iso = isoLocal(d);
            const evts = eventsOnDate(d);
            const cats = [...new Set(evts.map((e) => e.category))];
            const isSel = iso === selected;
            const isToday = iso === todayIso;
            return (
              <button
                key={iso}
                data-testid={`cal-day-${iso}`}
                onClick={() => setSelected(iso)}
                className={`aspect-square flex flex-col items-center justify-start pt-1.5 rounded-md text-sm transition-colors relative
                  ${isSel ? "bg-[#121212] text-[#F2E9D8]" : evts.length ? "hover:bg-stone-100 text-[#121212]" : "text-stone-400 hover:bg-stone-50"}`}
              >
                <span className={`${isToday && !isSel ? "w-6 h-6 rounded-full bg-[#C6FF33] text-[#121212] flex items-center justify-center font-bold" : ""}`}>{d.getDate()}</span>
                <span className="flex gap-0.5 mt-1 flex-wrap justify-center px-1">
                  {cats.slice(0, 4).map((c) => (
                    <span key={c} className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: catColor(c) }} />
                  ))}
                </span>
              </button>
            );
          })}
        </div>
        <div className="mt-6 pt-5 border-t border-stone-200 flex flex-wrap gap-x-4 gap-y-2">
          {CATEGORIES.map((c) => (
            <span key={c} className="inline-flex items-center gap-1.5 text-xs text-stone-600">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: catColor(c) }} /> {catLabel(c)}
            </span>
          ))}
        </div>
      </div>

      <div className="lg:col-span-2 bg-[#121212] text-[#F2E9D8] p-6 sm:p-7" data-testid="cal-day-panel">
        <p className="text-xs tracking-[0.25em] uppercase text-[#C6FF33] font-bold mb-1">
          {selected ? parseISO(selected).toLocaleDateString("en-US", { weekday: "long" }) : "Select a date"}
        </p>
        <h3 className="font-serif font-black text-3xl mb-6">
          {selected ? parseISO(selected).toLocaleDateString("en-US", { month: "long", day: "numeric" }) : "—"}
        </h3>
        {selectedEvents.length === 0 ? (
          <p className="text-[#F2E9D8]/60 text-sm">Nothing scheduled on this day. Pick another date to see what's happening.</p>
        ) : (
          <div className="space-y-3">
            {selectedEvents.map((e) => (
              <Link key={e.id} to={e.link} data-testid={`cal-event-${e.id}`} className="block border border-white/15 hover:border-[#C6FF33] p-4 transition-colors group">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: catColor(e.category) }} />
                  <span className="text-[10px] tracking-[0.15em] uppercase text-[#F2E9D8]/60">{e.category}</span>
                </div>
                <p className="font-serif font-bold text-lg leading-tight">{e.title}</p>
                <p className="text-sm text-[#F2E9D8]/70 mt-1">{e.time}{e.type === "open_studio" ? " · multiple sessions" : ""} · {e.type === "open_studio" ? `from $${e.price}` : `$${e.price}`}</p>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <span className="inline-flex items-center gap-1.5 text-[#C6FF33] text-xs font-bold group-hover:gap-2.5 transition-all">Book this <ArrowRight size={13} /></span>
                  <AddToCalendar event={e} dark />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CampWeekPromo({ b }) {
  return (
    <div data-testid={`camp-week-promo-${b.id}`} className="mt-8 bg-[#E0218A] text-white p-7 sm:p-9 flex flex-col md:flex-row md:items-center justify-between gap-5 border-2 border-[#121212]">
      <div className="max-w-xl">
        <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#C6FF33]">Kids Camp Week</p>
        <h3 className="font-serif font-black text-3xl leading-tight mt-1">{b.title}</h3>
        <p className="mt-2 text-white/85">Book one, two, or all {b.camp_ids.length} camps.{b.all_camp_price ? ` All ${b.camp_ids.length} for just $${b.all_camp_price} per child.` : ""}</p>
      </div>
      <Link to={`/camps/${b.slug}`} data-testid={`camp-week-promo-btn-${b.id}`}
        className="shrink-0 inline-flex items-center gap-2 px-7 py-3.5 bg-[#C6FF33] text-[#121212] hover:bg-[#b3ec1f] text-xs tracking-[0.2em] uppercase font-bold transition-colors">
        Build your camp week <ArrowRight size={15} />
      </Link>
    </div>
  );
}

export default function Classes() {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All Classes");
  const [bundles, setBundles] = useState([]);

  useEffect(() => {
    api.get("/classes").then(({ data }) => setClasses(data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/camp-bundles").then(({ data }) => setBundles(data)).catch(() => {});
  }, []);

  const cards = useMemo(() => {
    const classEvents = classes.map(classToEvent);
    const list = filter === "All Classes" ? classEvents : classEvents.filter((e) => e.category === filter);
    return list.sort((a, b) => a.date.localeCompare(b.date));
  }, [classes, filter]);

  const banner = BANNERS[filter];
  const showOpenStudioPromo = filter === "All Classes" || filter === "Open Studio";

  return (
    <div data-testid="classes-page" className="pt-32 pb-24 fade-in">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        {/* Hero */}
        <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#121212] mb-4">The Cake Society studio</p>
        <h1 className="font-serif font-black text-5xl sm:text-6xl text-[#121212] max-w-4xl leading-[1.05]">
          Cake is the culture. Come create with us.
        </h1>
        <p className="mt-6 text-lg text-stone-600 max-w-2xl">
          Join us for hands-on cake decorating classes, kids camps, Guest Creator workshops, Open Studio, and special workshops.
          Whether you're a beginner or an experienced decorator, you'll leave with new skills, new inspiration, and something you're proud to take home.
        </p>

        {/* Filters */}
        <div className="mt-10 flex flex-wrap gap-2.5" data-testid="class-filters">
          {["All Classes", ...CATEGORIES].map((f) => {
            const active = filter === f;
            return (
              <button
                key={f}
                data-testid={`filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setFilter(f)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs tracking-[0.12em] uppercase font-bold border-2 transition-colors
                  ${active ? "bg-[#121212] text-[#F2E9D8] border-[#121212]" : "bg-white text-[#121212] border-[#121212]/15 hover:border-[#121212]"}`}
              >
                {f !== "All Classes" && <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: catColor(f) }} />}
                {f === "All Classes" ? f : catLabel(f)}
              </button>
            );
          })}
        </div>

        {/* Open Studio promo */}
        {showOpenStudioPromo && (
          <div data-testid="open-studio-promo" className="mt-8 bg-[#121212] text-[#F2E9D8] p-7 sm:p-9 flex flex-col md:flex-row md:items-center justify-between gap-5">
            <div className="max-w-xl">
              <h3 className="font-serif font-black text-3xl leading-tight">Want to drop in and create a cake on your own?</h3>
              <p className="mt-2 text-[#F2E9D8]/75">Visit Open Studio to choose a date, pick your project, and book your spot.</p>
            </div>
            <Link
              to="/open-studio"
              data-testid="promo-book-open-studio"
              className="shrink-0 inline-flex items-center gap-2 px-7 py-3.5 bg-[#C6FF33] text-[#121212] hover:bg-[#b3ec1f] text-xs tracking-[0.2em] uppercase font-bold transition-colors"
            >
              Book Open Studio <ArrowRight size={15} />
            </Link>
          </div>
        )}

        {(filter === "All Classes" || filter === "Kids Camps") && bundles.map((b) => <CampWeekPromo key={b.id} b={b} />)}

        {/* Program banner (Guest Creators) */}
        {banner && (
          <div data-testid="program-banner" className="mt-8 bg-[#121212] text-[#F2E9D8] p-7 sm:p-9 flex flex-col md:flex-row md:items-center justify-between gap-5">
            <div className="max-w-xl">
              <h3 className="font-serif font-black text-3xl flex items-center gap-3">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: catColor(filter) }} />{catLabel(filter)}
              </h3>
              <p className="mt-2 text-[#F2E9D8]/75 italic">"{banner.desc}"</p>
            </div>
            <div className="flex flex-wrap gap-3 shrink-0">
              {banner.actions.map((a) => (
                <Link
                  key={a.label}
                  to={a.to}
                  data-testid={`banner-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold transition-colors ${a.primary ? "bg-[#C6FF33] text-[#121212] hover:bg-[#b3ec1f]" : "border-2 border-[#F2E9D8] text-[#F2E9D8] hover:bg-[#F2E9D8] hover:text-[#121212]"}`}
                >
                  {a.label}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Cards */}
        {loading ? (
          <div className="mt-16 text-stone-500">Loading the schedule…</div>
        ) : cards.length === 0 ? (
          <div className="mt-12 border-2 border-dashed border-[#121212]/20 p-12 text-center">
            <p className="text-stone-600">Nothing scheduled in this category yet — check back soon or browse the full calendar below.</p>
          </div>
        ) : (
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cards.map((e) => <EventCard key={e.id} e={e} />)}
          </div>
        )}

        {/* Class Schedule */}
        <div className="mt-24 pt-16 border-t-2 border-[#121212]">
          <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#121212] mb-3 inline-flex items-center gap-2"><CalendarDays size={14} /> Plan by date</p>
          <h2 className="font-serif font-black text-4xl sm:text-5xl text-[#121212]">Class Schedule</h2>
          <p className="mt-4 text-lg text-stone-600 max-w-2xl">
            Prefer to plan by date? Browse everything happening at Cake Society on our interactive class schedule.
          </p>
          <div className="mt-10">
            <ScheduleCalendar classes={classes} />
          </div>
        </div>
      </div>
    </div>
  );
}
